/* eslint-disable react-hooks/exhaustive-deps */
import { useMemo, useRef, useState } from 'react';
import { connect } from 'dva';
import { useIntl } from 'react-intl';
import {
  Button,
  Card,
  Col,
  DatePicker,
  Empty,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  message,
} from 'antd';
import {
  SearchOutlined,
  QuestionCircleOutlined,
  LinkOutlined,
} from '@ant-design/icons';
import ProTable from '@src/packages/pro-table/Table';
import { ActionType } from '@src/packages/pro-table';
import dayjs from 'dayjs';
import customerCardMapApi from './customerCardMapApi';
import { DEFAULT_CUSTOMER_PAGE_ID } from '@src/constants/constants';

const CCM_BUTTON_MODE = {
  TOGGLE: 'toggle',
} as const;

const LEDGER_REASON_OPTIONS = [
  'import',
  'hold',
  'redeem',
  'release',
  'released',
  'refund',
] as const;

const formatDigit = (val: any) => {
  if (val == null || val === '') return '-';
  return `${val}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
};

const formatTs = (val: any) => {
  if (!val) return '-';
  const d = dayjs(val);
  if (!d.isValid()) return '-';
  return d.format('DD/MM/YYYY HH:mm');
};

const getSourcePoints = (row: any) => {
  const source = row?.source;
  if (!source) return null;
  if (row.sourceType === 'card') return source.totalPoints;
  return source.bonusPoints;
};

const isExpired = (val: any) => {
  if (!val) return false;
  const d = dayjs(val);
  return d.isValid() && d.isBefore(dayjs());
};

const LEDGER_REASON_LABEL: Record<string, string> = {
  import: 'customerCardMap.ledger.reason.import',
  hold: 'customerCardMap.ledger.reason.hold',
  redeem: 'customerCardMap.ledger.reason.redeem',
  release: 'customerCardMap.ledger.reason.release',
  released: 'customerCardMap.ledger.reason.released',
  refund: 'customerCardMap.ledger.reason.refund',
};

const toNum = (v: any): number | undefined => {
  if (v == null || v === '') return undefined;
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : undefined;
};

const toBool = (v: any): boolean | undefined => {
  if (v === undefined || v === null || v === '') return undefined;
  if (v === true || v === 'true' || v === '1' || v === 1) return true;
  if (v === false || v === 'false' || v === '0' || v === 0) return false;
  return undefined;
};

const findPageButton = (pageInfo: any, mode: string) => {
  const buttons = Array.isArray(pageInfo?.buttons) ? pageInfo.buttons : [];
  return buttons.find((b: any) => String(b?.mode || '') === mode) || null;
};

/** Nếu page chưa cấu hình button mode=toggle → cho phép; có cấu hình thì check roles */
const canToggleMap = (pageInfo: any, roleId?: number | null) => {
  const buttons = Array.isArray(pageInfo?.buttons) ? pageInfo.buttons : [];
  const hasToggleCfg = buttons.some(
    (b: any) => String(b?.mode || '') === CCM_BUTTON_MODE.TOGGLE
  );
  if (!hasToggleCfg) return true;
  const btn = findPageButton(pageInfo, CCM_BUTTON_MODE.TOGGLE);
  if (!btn) return false;
  const roles = btn.roles;
  if (!Array.isArray(roles) || roles.length === 0) return true;
  if (roleId == null) return false;
  return roles.map((r: any) => Number(r)).includes(Number(roleId));
};

const CustomerCardMapListCtrl = (props: any) => {
  const intl = useIntl();
  const t = (id: string, values?: Record<string, any>) =>
    intl.formatMessage({ id }, values);

  const query = props.query || {};
  const gridSettings = props.pageInfo?.settings?.grid || {};
  const customerPageId =
    DEFAULT_CUSTOMER_PAGE_ID || gridSettings.customerPageId || 82;
  const pointLedgerPageId = gridSettings.pointLedgerPageId;

  const actionRef = useRef<ActionType>();
  const [phone, setPhone] = useState(String(query.phone || ''));
  const [cardNumber, setCardNumber] = useState(String(query.cardNumber || ''));
  const [customerId, setCustomerId] = useState<number | undefined>(
    toNum(query.customerId)
  );
  const [sourceId, setSourceId] = useState<number | undefined>(
    toNum(query.sourceId)
  );
  const [sourceType, setSourceType] = useState<
    'card' | 'cardCampaign' | undefined
  >(
    query.sourceType === 'card' || query.sourceType === 'cardCampaign'
      ? query.sourceType
      : undefined
  );
  const [linkType, setLinkType] = useState<string | undefined>(
    query.linkType ? String(query.linkType) : undefined
  );
  const [isActive, setIsActive] = useState<boolean | undefined>(
    toBool(query.isActive)
  );
  const [togglingId, setTogglingId] = useState<number | null>(null);
  const [ledgerModal, setLedgerModal] = useState<{
    open: boolean;
    loading: boolean;
    rows: any[];
    title: string;
    sourceType?: 'card' | 'cardCampaign';
    sourceId?: number;
    current: number;
    pageSize: number;
    total: number;
  }>({
    open: false,
    loading: false,
    rows: [],
    title: '',
    current: 1,
    pageSize: 20,
    total: 0,
  });
  const [ledgerDirection, setLedgerDirection] = useState<
    'credit' | 'debit' | undefined
  >(undefined);
  const [ledgerReason, setLedgerReason] = useState<string | undefined>(
    undefined
  );
  const [ledgerDateRange, setLedgerDateRange] = useState<any>(null);

  const roleId =
    props.authUser?.roleId != null ? Number(props.authUser.roleId) : null;
  const allowToggle = canToggleMap(props.pageInfo, roleId);

  const reload = () => actionRef.current?.reload();

  const clearFilters = () => {
    setPhone('');
    setCardNumber('');
    setCustomerId(undefined);
    setSourceId(undefined);
    setSourceType(undefined);
    setLinkType(undefined);
    setIsActive(undefined);
    setTimeout(() => reload(), 0);
  };

  const openCustomer = (row: any) => {
    const id = row?.customerId || row?.customer?.id;
    if (!id) return;
    if (customerPageId) {
      const filter = encodeURIComponent(JSON.stringify({ id }));
      window.location.hash = `#/list?page=${customerPageId}&filter=${filter}`;
      return;
    }
    message.info(t('customerCardMap.msg.noCustomerPage', { id }));
  };

  const fetchPointLedgers = async (opts: {
    sourceType?: 'card' | 'cardCampaign';
    sourceId?: number;
    direction?: 'credit' | 'debit';
    reason?: string;
    dateRange?: any;
    current?: number;
    pageSize?: number;
  }) => {
    const {
      sourceType,
      sourceId,
      direction,
      reason,
      dateRange,
      current = 1,
      pageSize = 20,
    } = opts;
    const from = dateRange?.[0];
    const to = dateRange?.[1];
    const createdFrom =
      from && typeof from.isValid === 'function' && from.isValid()
        ? from.clone().startOf('day').toISOString()
        : undefined;
    const createdTo =
      to && typeof to.isValid === 'function' && to.isValid()
        ? to.clone().endOf('day').toISOString()
        : undefined;

    setLedgerModal((s) => ({
      ...s,
      loading: true,
      current,
      pageSize,
    }));
    const res = await customerCardMapApi.pointLedgers({
      sourceType,
      sourceId,
      direction,
      reason,
      createdFrom,
      createdTo,
      limit: pageSize,
      skip: (current - 1) * pageSize,
    });
    if (!res.ok) {
      message.error(res.message || t('customerCardMap.msg.error'));
      setLedgerModal((s) => ({
        ...s,
        loading: false,
        rows: [],
        total: 0,
      }));
      return;
    }
    setLedgerModal((s) => ({
      ...s,
      loading: false,
      rows: res.data?.rows || [],
      total: res.data?.total || 0,
    }));
  };

  const openPointLedger = async (row: any) => {
    if (pointLedgerPageId && row?.sourceType && row?.sourceId) {
      const qs = [
        `page=${pointLedgerPageId}`,
        `sourceType=${row.sourceType}`,
        `sourceId=${row.sourceId}`,
      ].join('&');
      window.location.hash = `#/list?${qs}`;
      return;
    }

    setLedgerDirection(undefined);
    setLedgerReason(undefined);
    setLedgerDateRange(null);
    setLedgerModal({
      open: true,
      loading: true,
      rows: [],
      title: t('customerCardMap.ledger.title', {
        type: row.sourceType,
        id: row.sourceId,
      }),
      sourceType: row.sourceType,
      sourceId: row.sourceId,
      current: 1,
      pageSize: 20,
      total: 0,
    });
    await fetchPointLedgers({
      sourceType: row.sourceType,
      sourceId: row.sourceId,
      current: 1,
      pageSize: 20,
    });
  };

  const applyLedgerFilters = () => {
    fetchPointLedgers({
      sourceType: ledgerModal.sourceType,
      sourceId: ledgerModal.sourceId,
      direction: ledgerDirection,
      reason: ledgerReason,
      dateRange: ledgerDateRange,
      current: 1,
      pageSize: ledgerModal.pageSize,
    });
  };

  const clearLedgerFilters = () => {
    setLedgerDirection(undefined);
    setLedgerReason(undefined);
    setLedgerDateRange(null);
    fetchPointLedgers({
      sourceType: ledgerModal.sourceType,
      sourceId: ledgerModal.sourceId,
      current: 1,
      pageSize: ledgerModal.pageSize,
    });
  };

  const onLedgerPageChange = (page: number, pageSize?: number) => {
    const nextSize = pageSize || ledgerModal.pageSize;
    fetchPointLedgers({
      sourceType: ledgerModal.sourceType,
      sourceId: ledgerModal.sourceId,
      direction: ledgerDirection,
      reason: ledgerReason,
      dateRange: ledgerDateRange,
      current: page,
      pageSize: nextSize,
    });
  };

  const toggleActive = (row: any) => {
    if (!allowToggle) return;
    const nextActive = !row.isActive;
    Modal.confirm({
      title: nextActive
        ? t('customerCardMap.confirm.enableTitle')
        : t('customerCardMap.confirm.disableTitle'),
      content: nextActive
        ? t('customerCardMap.confirm.enableContent', { id: row.id })
        : t('customerCardMap.confirm.disableContent', { id: row.id }),
      okText: t('customerCardMap.action.confirm'),
      cancelText: t('customerCardMap.action.cancel'),
      onOk: async () => {
        setTogglingId(row.id);
        try {
          const res = await customerCardMapApi.setActive({
            id: row.id,
            isActive: nextActive,
          });
          if (!res.ok) {
            message.error(res.message || t('customerCardMap.msg.error'));
            return;
          }
          message.success(
            nextActive
              ? t('customerCardMap.msg.enabled')
              : t('customerCardMap.msg.disabled')
          );
          reload();
        } finally {
          setTogglingId(null);
        }
      },
    });
  };

  const columns: any[] = useMemo(
    () => [
      {
        title: t('customerCardMap.col.id'),
        dataIndex: 'id',
        width: 70,
        fixed: 'left',
        search: false,
      },
      {
        title: t('customerCardMap.col.customer'),
        dataIndex: 'customer',
        width: 200,
        fixed: 'left',
        search: false,
        render: (_: any, row: any) => {
          const c = row?.customer;
          if (!c) return '-';
          const label =
            [c.phone, c.name].filter(Boolean).join(' · ') || `#${c.id}`;
          return (
            <Space size={4}>
              <Tooltip
                title={`ID: ${c.id}${c.merchantRef ? ` · ${c.merchantRef}` : ''
                  }`}
              >
                <span>{label}</span>
              </Tooltip>
              <Tooltip title={t('customerCardMap.action.openCustomer')}>
                <a onClick={() => openCustomer(row)}>
                  <LinkOutlined />
                </a>
              </Tooltip>
            </Space>
          );
        },
      },
      {
        title: t('customerCardMap.col.sourceType'),
        dataIndex: 'sourceType',
        width: 120,
        search: false,
        render: (val: string) =>
          val === 'cardCampaign' ? (
            <Tag color="purple">{t('customerCardMap.source.campaign')}</Tag>
          ) : (
            <Tag color="blue">{t('customerCardMap.source.card')}</Tag>
          ),
      },
      {
        title: t('customerCardMap.col.sourceId'),
        dataIndex: 'sourceId',
        width: 90,
        search: false,
      },
      {
        title: t('customerCardMap.col.cardNumber'),
        dataIndex: ['source', 'cardNumber'],
        width: 160,
        search: false,
        render: (_: any, row: any) => row?.source?.cardNumber || '-',
      },
      {
        title: t('customerCardMap.col.sourceName'),
        dataIndex: ['source', 'customerName'],
        width: 140,
        ellipsis: true,
        search: false,
        render: (_: any, row: any) => row?.source?.customerName || '-',
      },
      {
        title: t('customerCardMap.col.points'),
        dataIndex: 'points',
        width: 110,
        search: false,
        render: (_: any, row: any) => formatDigit(getSourcePoints(row)),
      },
      {
        title: t('customerCardMap.col.expireAt'),
        dataIndex: ['source', 'pointExpireAt'],
        width: 150,
        search: false,
        render: (_: any, row: any) => {
          const exp = row?.source?.pointExpireAt;
          const expired = isExpired(exp);
          const text = formatTs(exp);
          if (text === '-') return text;
          return (
            <Tooltip
              title={
                expired
                  ? t('customerCardMap.expire.expired')
                  : t('customerCardMap.expire.valid')
              }
            >
              <span style={{ color: expired ? '#cf1322' : undefined }}>
                {text}
                {expired ? ` · ${t('customerCardMap.expire.tag')}` : ''}
              </span>
            </Tooltip>
          );
        },
      },
      {
        title: (
          <span>
            {t('customerCardMap.col.linkType')}{' '}
            <Tooltip
              title={
                <div>
                  <div>{t('customerCardMap.linkType.tip.phone')}</div>
                  <div>{t('customerCardMap.linkType.tip.cardNumber')}</div>
                </div>
              }
            >
              <QuestionCircleOutlined style={{ color: 'rgba(0,0,0,.45)' }} />
            </Tooltip>
          </span>
        ),
        dataIndex: 'linkType',
        width: 120,
        search: false,
        render: (val: string) => {
          if (!val) return '-';
          const tipId =
            val === 'phone'
              ? 'customerCardMap.linkType.tip.phone'
              : val === 'cardNumber'
                ? 'customerCardMap.linkType.tip.cardNumber'
                : 'customerCardMap.linkType.tip.other';
          return (
            <Tooltip title={t(tipId, { type: val })}>
              <Tag>{val}</Tag>
            </Tooltip>
          );
        },
      },
      {
        title: t('customerCardMap.col.linkValue'),
        dataIndex: 'linkValue',
        width: 130,
        ellipsis: true,
        search: false,
        render: (val: string) => val || '-',
      },
      {
        title: t('customerCardMap.col.isActive'),
        dataIndex: 'isActive',
        width: 100,
        search: false,
        render: (val: boolean) =>
          val ? (
            <Tag color="green">{t('customerCardMap.status.active')}</Tag>
          ) : (
            <Tag color="default">{t('customerCardMap.status.inactive')}</Tag>
          ),
      },
      {
        title: t('customerCardMap.col.createdAt'),
        dataIndex: 'createdAt',
        width: 140,
        search: false,
        render: (_: any, row: any) => formatTs(row?.createdAt),
      },
      {
        title: t('customerCardMap.col.actions'),
        valueType: 'option',
        width: allowToggle ? 220 : 130,
        fixed: 'right',
        render: (_: any, row: any) => {
          const actions: any[] = [
            <a key="ledger" onClick={() => openPointLedger(row)}>
              {t('customerCardMap.action.ledgers')}
            </a>,
          ];
          if (allowToggle) {
            actions.push(
              <a
                key="toggle"
                onClick={() => toggleActive(row)}
                style={{
                  opacity: togglingId === row.id ? 0.5 : 1,
                  pointerEvents: togglingId === row.id ? 'none' : 'auto',
                  whiteSpace: 'nowrap',
                }}
              >
                {row.isActive
                  ? t('customerCardMap.action.disable')
                  : t('customerCardMap.action.enable')}
              </a>
            );
          }
          return (
            <Space size={8} wrap>
              {actions}
            </Space>
          );
        },
      },
    ],
    [allowToggle, togglingId, customerPageId, pointLedgerPageId, intl.locale]
  );

  const ledgerColumns = [
    { title: 'ID', dataIndex: 'id', width: 70 },
    {
      title: t('customerCardMap.col.createdAt'),
      dataIndex: 'createdAt',
      width: 140,
      render: (v: any) => formatTs(v),
    },
    {
      title: t('customerCardMap.ledger.col.direction'),
      dataIndex: 'direction',
      width: 110,
      render: (val: string) => {
        if (val === 'credit') {
          return (
            <Tag color="success">
              {t('customerCardMap.ledger.direction.credit')}
            </Tag>
          );
        }
        if (val === 'debit') {
          return (
            <Tag color="error">
              {t('customerCardMap.ledger.direction.debit')}
            </Tag>
          );
        }
        return val || '-';
      },
    },
    {
      title: t('customerCardMap.ledger.col.amount'),
      dataIndex: 'amount',
      width: 120,
      render: (v: any, row: any) => {
        const isCredit = row?.direction === 'credit';
        const isDebit = row?.direction === 'debit';
        const prefix = isCredit ? '+' : isDebit ? '-' : '';
        return (
          <span
            style={{
              color: isCredit ? '#389e0d' : isDebit ? '#cf1322' : undefined,
              fontWeight: 600,
            }}
          >
            {prefix}
            {formatDigit(v)}
          </span>
        );
      },
    },
    {
      title: t('customerCardMap.ledger.col.balanceAfter'),
      dataIndex: 'balanceAfter',
      width: 120,
      render: (v: any) => formatDigit(v),
    },
    {
      title: t('customerCardMap.ledger.col.reason'),
      dataIndex: 'reason',
      width: 140,
      render: (_: any, row: any) => {
        const reason = row?.reason || row?.meta?.reason;
        if (!reason) return '-';
        const labelId = LEDGER_REASON_LABEL[String(reason)];
        return labelId ? t(labelId) : reason;
      },
    },
    {
      title: t('customerCardMap.ledger.col.note'),
      dataIndex: 'note',
      render: (_: any, row: any) =>
        row?.note || row?.meta?.note || '-',
    }
  ];

  const filterFullWidth = { width: '100%' as const };
  const filterLabelStyle = {
    marginBottom: 4,
    fontSize: 12,
    color: '#8c8c8c',
  };
  // Equal width: 1 / 2 / 3 / 4 columns by breakpoint
  const filterCol = { xs: 24, sm: 12, md: 8, lg: 6, xl: 6 };

  return (
    <div className="customer-card-map-list">
      <Card
        size="small"
        bordered={false}
        style={{ marginBottom: 16 }}
        bodyStyle={{ paddingBottom: 8 }}
      >
        <Row gutter={[12, 12]} align="bottom">
          <Col {...filterCol}>
            <div style={filterLabelStyle}>
              {t('customerCardMap.search.phone')}
            </div>
            <Input
              allowClear
              placeholder={t('customerCardMap.search.phone')}
              prefix={<SearchOutlined />}
              style={filterFullWidth}
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              onPressEnter={() => reload()}
            />
          </Col>
          <Col {...filterCol}>
            <div style={filterLabelStyle}>
              {t('customerCardMap.search.cardNumber')}
            </div>
            <Input
              allowClear
              placeholder={t('customerCardMap.search.cardNumber')}
              style={filterFullWidth}
              value={cardNumber}
              onChange={(e) => setCardNumber(e.target.value)}
              onPressEnter={() => reload()}
            />
          </Col>
          <Col {...filterCol}>
            <div style={filterLabelStyle}>
              {t('customerCardMap.search.customerId')}
            </div>
            <InputNumber
              min={1}
              placeholder={t('customerCardMap.search.customerId')}
              style={filterFullWidth}
              value={customerId}
              onChange={(v) =>
                setCustomerId(typeof v === 'number' ? v : undefined)
              }
            />
          </Col>
          <Col {...filterCol}>
            <div style={filterLabelStyle}>
              {t('customerCardMap.search.sourceId')}
            </div>
            <InputNumber
              min={1}
              placeholder={t('customerCardMap.search.sourceId')}
              style={filterFullWidth}
              value={sourceId}
              onChange={(v) =>
                setSourceId(typeof v === 'number' ? v : undefined)
              }
            />
          </Col>
          <Col {...filterCol}>
            <div style={filterLabelStyle}>
              {t('customerCardMap.search.sourceType')}
            </div>
            <Select
              allowClear
              placeholder={t('customerCardMap.search.sourceType')}
              style={filterFullWidth}
              value={sourceType}
              onChange={(v) => setSourceType(v)}
            >
              <Select.Option value="card">
                {t('customerCardMap.source.card')}
              </Select.Option>
              <Select.Option value="cardCampaign">
                {t('customerCardMap.source.campaign')}
              </Select.Option>
            </Select>
          </Col>
          <Col {...filterCol}>
            <div style={filterLabelStyle}>
              {t('customerCardMap.search.linkType')}
            </div>
            <Select
              allowClear
              placeholder={t('customerCardMap.search.linkType')}
              style={filterFullWidth}
              value={linkType}
              onChange={(v) => setLinkType(v)}
            >
              <Select.Option value="phone">phone</Select.Option>
              <Select.Option value="cardNumber">cardNumber</Select.Option>
            </Select>
          </Col>
          <Col {...filterCol}>
            <div style={filterLabelStyle}>
              {t('customerCardMap.search.isActive')}
            </div>
            <Select
              allowClear
              placeholder={t('customerCardMap.search.isActive')}
              style={filterFullWidth}
              value={
                isActive === undefined
                  ? undefined
                  : isActive
                    ? 'true'
                    : 'false'
              }
              onChange={(v) => {
                setIsActive(v === undefined ? undefined : v === 'true');
              }}
            >
              <Select.Option value="true">
                {t('customerCardMap.status.active')}
              </Select.Option>
              <Select.Option value="false">
                {t('customerCardMap.status.inactive')}
              </Select.Option>
            </Select>
          </Col>
          <Col {...filterCol}>
            <Space wrap>
              <Button
                type="primary"
                onClick={() => reload()}
              >
                {t('customerCardMap.action.filter')}
              </Button>
              <Button onClick={clearFilters}>
                {t('customerCardMap.action.clear')}
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      <ProTable
        headerTitle={props.pageInfo?.name || t('customerCardMap.title')}
        actionRef={actionRef as any}
        rowKey="id"
        search={false}
        options={{ reload: true, density: true, setting: true }}
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={t('customerCardMap.empty')}
            />
          ),
        }}
        toolBarRender={() => []}
        request={async (params: any) => {
          const pageSize = params.pageSize || 20;
          const current = params.current || 1;
          const res = await customerCardMapApi.list({
            skip: (current - 1) * pageSize,
            limit: pageSize,
            phone: phone || undefined,
            cardNumber: cardNumber || undefined,
            customerId,
            sourceId,
            sourceType,
            linkType,
            isActive,
          });
          if (!res.ok) {
            message.error(res.message || t('customerCardMap.msg.error'));
            return { data: [], success: false, total: 0 };
          }
          return {
            data: res.data?.rows || [],
            success: true,
            total: res.data?.total || 0,
          };
        }}
        columns={columns}
        pagination={{
          defaultPageSize: 20,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50'],
        }}
        scroll={{ x: 1800 }}
      />

      <Modal
        title={ledgerModal.title}
        visible={ledgerModal.open}
        onCancel={() => setLedgerModal((s) => ({ ...s, open: false }))}
        footer={null}
        width={960}
        destroyOnClose
      >
        <Row gutter={[12, 12]} style={{ marginBottom: 12 }} align="bottom">
          <Col xs={24} sm={12} md={8}>
            <div style={filterLabelStyle}>
              {t('customerCardMap.ledger.filter.createdAt')}
            </div>
            <DatePicker.RangePicker
              style={{ width: '100%' }}
              format="DD/MM/YYYY"
              value={ledgerDateRange as any}
              onChange={(vals) =>
                setLedgerDateRange(
                  vals ? [vals[0] || null, vals[1] || null] : null
                )
              }
              allowClear
            />
          </Col>
          <Col xs={12} sm={6} md={5}>
            <div style={filterLabelStyle}>
              {t('customerCardMap.ledger.col.direction')}
            </div>
            <Select
              allowClear
              placeholder={t('customerCardMap.ledger.col.direction')}
              style={{ width: '100%' }}
              value={ledgerDirection}
              onChange={(v) => setLedgerDirection(v)}
            >
              <Select.Option value="credit">
                {t('customerCardMap.ledger.direction.credit')}
              </Select.Option>
              <Select.Option value="debit">
                {t('customerCardMap.ledger.direction.debit')}
              </Select.Option>
            </Select>
          </Col>
          <Col xs={12} sm={6} md={5}>
            <div style={filterLabelStyle}>
              {t('customerCardMap.ledger.col.reason')}
            </div>
            <Select
              allowClear
              placeholder={t('customerCardMap.ledger.col.reason')}
              style={{ width: '100%' }}
              value={ledgerReason}
              onChange={(v) => setLedgerReason(v)}
            >
              {LEDGER_REASON_OPTIONS.map((key) => (
                <Select.Option key={key} value={key}>
                  {t(LEDGER_REASON_LABEL[key])}
                </Select.Option>
              ))}
            </Select>
          </Col>
          <Col xs={24} sm={24} md={6}>
            <Space wrap>
              <Button type="primary" onClick={applyLedgerFilters}>
                {t('customerCardMap.action.filter')}
              </Button>
              <Button onClick={clearLedgerFilters}>
                {t('customerCardMap.action.clear')}
              </Button>
            </Space>
          </Col>
        </Row>
        <Table
          rowKey="id"
          size="small"
          loading={ledgerModal.loading}
          dataSource={ledgerModal.rows}
          columns={ledgerColumns}
          pagination={{
            current: ledgerModal.current,
            pageSize: ledgerModal.pageSize,
            total: ledgerModal.total,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50'],
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} / ${total}`,
            onChange: onLedgerPageChange,
            onShowSizeChange: onLedgerPageChange,
          }}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={t('customerCardMap.ledger.empty')}
              />
            ),
          }}
          scroll={{ x: 800, y: 420 }}
        />
      </Modal>
    </div>
  );
};

export default connect(({ auth }: any) => ({
  authUser: auth.authUser,
}))(CustomerCardMapListCtrl);
